import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CollapsibleFilterBar from '@shared/components/CollapsibleFilterBar';
import CoverageFilter from '@shared/components/CoverageFilter';
import { PeriodSelector } from '@shared/pricing';
import { productTypeChipSx } from '@shared/constants/product';
import type { HoldingStatus } from '../types';

const TYPE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'All', value: '' },
  { label: 'AVCS', value: 'AVCS' },
  { label: 'ADP', value: 'ADP' },
  { label: 'e-NP', value: 'e-NP' },
];

export type BasketFilter = '' | 'inBasket' | 'notInBasket';
export type RouteFilter = '' | 'onRoute' | 'offRoute';

export interface ProductFilters {
  searchQuery: string;
  productType: string;
  holdingStatuses: HoldingStatus[];
  coverageValues: string[];
  routeFilter: RouteFilter;
  basketFilter: BasketFilter;
}

interface Props {
  filters: ProductFilters;
  onFiltersChange: (filters: ProductFilters) => void;
  activeFilterCount: number;
  periodValue: number;
  onPeriodChange: (months: number) => void;
}

export default function RouteProductFilter({
  filters,
  onFiltersChange,
  activeFilterCount,
  periodValue,
  onPeriodChange,
}: Props) {
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    onFiltersChange({ ...filters, searchQuery: event.target.value });
  }

  function handleHoldingStatusChange(event: SelectChangeEvent<HoldingStatus[]>) {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      holdingStatuses: typeof value === 'string' ? (value.split(',') as HoldingStatus[]) : value,
    });
  }

  function handleCoverageChange(values: string[]) {
    onFiltersChange({ ...filters, coverageValues: values });
  }

  function handleClearFilters() {
    onFiltersChange({
      searchQuery: '',
      productType: '',
      holdingStatuses: [],
      coverageValues: [],
      routeFilter: '',
      basketFilter: '',
    });
  }

  return (
    <Box sx={{ mb: 1 }}>
      {/* Row 1: Search + Period */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search products..."
          value={filters.searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <PeriodSelector
          value={periodValue}
          onChange={onPeriodChange}
          size="small"
          sx={{ minWidth: 100 }}
        />
      </Box>

      <CollapsibleFilterBar activeFilterCount={activeFilterCount} onClear={handleClearFilters}>
        {/* Product type chips */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {TYPE_OPTIONS.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              variant={filters.productType === opt.value ? 'filled' : 'outlined'}
              color={
                opt.value ? undefined : filters.productType === opt.value ? 'primary' : 'default'
              }
              sx={productTypeChipSx(opt.value, filters.productType === opt.value)}
              onClick={() => onFiltersChange({ ...filters, productType: opt.value })}
            />
          ))}
        </Box>

        {/* Row: Holding Status + Coverage */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Holding</InputLabel>
            <Select
              multiple
              value={filters.holdingStatuses}
              onChange={handleHoldingStatusChange}
              label="Holding"
              renderValue={(selected) => selected.join(', ')}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expiring">Expiring</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="none">None</MenuItem>
            </Select>
          </FormControl>

          <CoverageFilter
            value={filters.coverageValues}
            onChange={handleCoverageChange}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Row: Basket + On Route */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Basket</InputLabel>
            <Select
              value={filters.basketFilter}
              onChange={(e) =>
                onFiltersChange({ ...filters, basketFilter: e.target.value as BasketFilter })
              }
              label="Basket"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="inBasket">In Basket</MenuItem>
              <MenuItem value="notInBasket">Not in Basket</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>On Route</InputLabel>
            <Select
              value={filters.routeFilter}
              onChange={(e) =>
                onFiltersChange({ ...filters, routeFilter: e.target.value as RouteFilter })
              }
              label="On Route"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="onRoute">On Route</MenuItem>
              <MenuItem value="offRoute">Off Route</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </CollapsibleFilterBar>
    </Box>
  );
}
